import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getDlq, replayDlq } from '../api/executions';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/PageHeader';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';

export function DlqPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: executions = [], isLoading } = useQuery({
    queryKey: ['dlq'],
    queryFn: getDlq,
  });

  const replayMutation = useMutation({
    mutationFn: replayDlq,
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['dlq'] });
      navigate(`/executions/${res.id}`);
    },
    onError: (err: Error) => toast.error(`Replay failed: ${err.message}`),
  });

  return (
    <div>
      <PageHeader title="Dead letter queue" description="Failed executions waiting to be replayed." />
      {isLoading ? <p className="text-muted-foreground">Loading…</p> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Flow</TableHead>
              <TableHead>Error</TableHead>
              <TableHead>Failed at</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {executions.map(ex => (
              <TableRow key={ex.id}>
                <TableCell className="font-medium">{ex.flowName ?? ex.flowId}</TableCell>
                <TableCell className="text-destructive text-xs max-w-xs truncate" title={ex.error ?? ''}>
                  {ex.error ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">{new Date(ex.startedAt).toLocaleString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2 justify-end">
                    <Button
                      size="xs"
                      onClick={() => replayMutation.mutate(ex.id)}
                      disabled={replayMutation.isPending}
                    >
                      Replay
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => navigate(`/executions/${ex.id}`)}
                    >
                      View
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {executions.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="p-6 text-center text-muted-foreground">DLQ is empty</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
